import { useState, useEffect, useRef, useMemo } from "react";

// ── Model ─────────────────────────────────────────────────────────────────────
const ZETA = 0.5, DELTA = 0.025, CHI = 1.0;
const A_GOOD = 1.001, A_BAD = 0.999;
const MU = 3.5;
const ALPHA_SS_G = 0.78, ALPHA_SS_B = 0.15;
const N_PERIODS = 13;
// N_GOOD will be controlled by state
// const N_GOOD    = 8;
const FRAMES_PER_PERIOD = 40;
const TOTAL_FRAMES = N_PERIODS * FRAMES_PER_PERIOD;

// DEMO exaggeration: steepen saddle paths so visual contrast is obvious
const DEMO_STEEPEN = 6;
function gamma_fn(a) { return ZETA / (1 - (1 - DELTA) * a); }
function Y(alpha, A) {
  const g  = gamma_fn(alpha) * DEMO_STEEPEN;
  const g0 = gamma_fn(0)     * DEMO_STEEPEN;
  return Math.pow(A, g * (1 + CHI)) / Math.pow(1.0, g0 * (1 + CHI));
}

// Compute forecast from an arbitrary live alpha (for smooth CI)
function computeForecast(alphaLive, A_current, horizons = 5) {
  const fcG = [], fcB = [];
  let aG = alphaNext(alphaLive, A_current);
  let aB = alphaNext(alphaLive, A_current);
  for (let f = 1; f <= horizons; f++) {
    fcG.push({ t: f, y: Y(aG, A_GOOD) });
    fcB.push({ t: f, y: Y(aB, A_BAD)  });
    aG = alphaNext(aG, A_GOOD);
    aB = alphaNext(aB, A_BAD);
  }
  return { fcG, fcB };
}
function alphaNext(alpha, A) {
  const ss = A >= A_GOOD ? ALPHA_SS_G : ALPHA_SS_B;
  return Math.max(0.03, Math.min(0.95, alpha + (ss - alpha) / MU));
}

// Pre-compute period snapshots
// buildPeriods 已被 useMemo 替代，不再需要此函数和 PERIODS 常量

// ── Diagram constants ─────────────────────────────────────────────────────────
const N_FIRMS   = 48;
const ALL_FIRMS = Array.from({ length: N_FIRMS }, (_, i) => i / (N_FIRMS - 1));
const RECV_POS  = [0.20, 0.50, 0.80];

function arrowCount(alpha) {
  return Math.max(1, Math.round(alpha * N_FIRMS * 0.80));
}

const SUP_CACHE = {};
function getSuppliers(rid, n) {
  const key = `${rid.toFixed(2)}-${n}`;
  if (SUP_CACHE[key]) return SUP_CACHE[key];
  const pool = ALL_FIRMS.filter(s => Math.abs(s - rid) > 0.04);
  const result = [];
  for (let i = 0; i < pool.length && result.length < Math.min(n, 20); i++) {
    const idx = Math.floor(((rid * 37 + i * 13) * 97) % pool.length);
    const s = pool[idx];
    if (!result.includes(s)) result.push(s);
  }
  for (let i = 0; result.length < Math.min(n, 20); i++) {
    const s = pool[i % pool.length];
    if (!result.includes(s)) result.push(s);
  }
  SUP_CACHE[key] = result;
  return result;
}

// ── Colors ────────────────────────────────────────────────────────────────────
const C = {
  bd:"#1F3864", bm:"#2E5090", bl:"#5B8BD4",
  or:"#E67814", ol:"#F5A84B",
  gb:"#F4F6FB", gl:"#C8D0E4", gt:"#7880A0",
  wh:"#FFFFFF", sh:"rgba(230,120,20,0.10)",
};

// ── Arrow ─────────────────────────────────────────────────────────────────────
function Arrow({ x1, y1, x2, y2, color, op, sw, mid }) {
  const cx = (x1 + x2) / 2;
  const cy = y1 - 14 - Math.abs(x2 - x1) * 0.05;
  return (
    <path d={`M${x1},${y1} Q${cx},${cy} ${x2},${y2}`}
      fill="none" stroke={color} strokeWidth={sw} opacity={op}
      markerEnd={`url(#${mid})`} />
  );
}

function btnStyle(bg, ghost, extra = {}) {
  return {
    padding:"7px 16px", borderRadius:7, border:"none", cursor:"pointer",
    fontFamily:"inherit", fontWeight:"bold", fontSize:13,
    background: ghost ? "transparent" : bg,
    color: ghost ? bg : "#fff",
    boxShadow: ghost ? "none" : `0 2px 6px ${bg}55`,
    outline: ghost ? `1.5px solid ${bg}` : "none",
    transition:"all 0.15s", ...extra,
  };
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function App() {
    // 新增 shock hit 时间控制
    const [nGood, setNGood] = useState(8);
  const [frame, setFrame]     = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed]     = useState(1);
  const rafRef  = useRef(null);
  const lastRef = useRef(null);
  const accRef  = useRef(0);

  const period   = Math.min(Math.floor(frame / FRAMES_PER_PERIOD), N_PERIODS - 1);
  const progress = (frame % FRAMES_PER_PERIOD) / FRAMES_PER_PERIOD;
  // 动态生成 periods，依赖 nGood
  const PERIODS = useMemo(() => {
    const periods = [];
    let alpha = 0.12;
    for (let t = 0; t < N_PERIODS; t++) {
      const A    = t < nGood ? A_GOOD : A_BAD;
      const y    = Y(alpha, A);
      const aEnd = alphaNext(alpha, A);
      const fcG = [], fcB = [];
      let aG = aEnd, aB = aEnd;
      for (let f = 1; f <= 5; f++) {
        fcG.push({ t: t + f, y: Y(aG, A_GOOD) });
        fcB.push({ t: t + f, y: Y(aB, A_BAD) });
        aG = alphaNext(aG, A_GOOD);
        aB = alphaNext(aB, A_BAD);
      }
      periods.push({ t, A, y, alpha, alphaEnd: aEnd, isRec: A < 1, fcG, fcB });
      alpha = aEnd;
    }
    return periods;
  }, [nGood]);
  const pd = PERIODS[period];

  const ease = t => t < 0.5 ? 2*t*t : -1+(4-2*t)*t;
  const alpha = pd.alpha + (pd.alphaEnd - pd.alpha) * ease(progress);

  const Yg   = Y(alpha, A_GOOD);
  const Yb   = Y(alpha, A_BAD);
  const curY = Y(alpha, pd.A);

  // Playback
  useEffect(() => {
    if (!playing) { cancelAnimationFrame(rafRef.current); return; }
    let lastTs = null;
    function tick(ts) {
      if (lastTs === null) lastTs = ts;
      const frameAdvance = ((ts - lastTs) * speed) / 16;
      lastTs = ts;
      setFrame(f => {
        let next = f + frameAdvance;
        if (next >= TOTAL_FRAMES - 1) {
          next = 0;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, speed]);

  function reset() {
    setFrame(0); setPlaying(false);
    accRef.current = 0; lastRef.current = null;
  }

  // ── Diagram ────────────────────────────────────────────────────────────────
  const DW = 460, DH = 130;   // shorter height
  const LX1 = 30, LX2 = 430, LY = 82;
  const lineLen = LX2 - LX1;
  const fx = p => LX1 + p * lineLen;

  const n = arrowCount(alpha);
  // useMemo 缓存 arrowSets，只有 alpha 或 n 变化时才重新计算
  const arrowSets = useMemo(() => {
    return RECV_POS.map(rid => ({ rid, sups: getSuppliers(rid, n) }));
  }, [n]);
  const arrowCol = pd.isRec ? C.or : C.bm;
  const midId    = `ah${arrowCol.replace(/[^a-z0-9]/gi,"")}`;

  // ── t-Y plot ───────────────────────────────────────────────────────────────
  const PW = 700, PH = 260;
  const PM = { t:16, r:20, b:38, l:46 };
  const pw = PW - PM.l - PM.r, ph = PH - PM.t - PM.b;
  const yMin = 0.970, yMax = 1.030;

  function pxFn(t, prog=0) {
    return PM.l + ((t + prog) / N_PERIODS) * pw;
  }
  function pyFn(y) {
    // clamp y to [yMin, yMax] so nothing escapes the plot
    const yc = Math.max(yMin, Math.min(yMax, y));
    return PM.t + ph - ((yc - yMin) / (yMax - yMin)) * ph;
  }

  // History: dot at start of each completed period
  const histDots = PERIODS.slice(0, period+1).map(p => ({
    x: pxFn(p.t), y: pyFn(p.y), rec: p.isRec,
  }));
  const curDot = { x: pxFn(period, progress), y: pyFn(curY) };
  const allPts = [...histDots.map(d=>`${d.x},${d.y}`), `${curDot.x},${curDot.y}`];
  const histPath = allPts.length > 1 ? "M"+allPts.join("L") : null;

  // Forecast computed from LIVE alpha every frame → smooth CI
  const { fcG, fcB } = computeForecast(alpha, pd.A);
  // Map forecast offset to absolute plot x, anchored at current moving position
  function pxFc(offset) { return pxFn(period + progress + offset); }
  const fanPts = [
    `${curDot.x},${curDot.y}`,
    ...fcG.map(p=>`${pxFc(p.t)},${pyFn(p.y)}`),
    ...fcB.slice().reverse().map(p=>`${pxFc(p.t)},${pyFn(p.y)}`),
  ];
  const fanPath  = `M${fanPts.join("L")}Z`;
  const fcGPath  = "M"+[`${curDot.x},${curDot.y}`,...fcG.map(p=>`${pxFc(p.t)},${pyFn(p.y)}`)].join("L");
  const fcBPath  = "M"+[`${curDot.x},${curDot.y}`,...fcB.map(p=>`${pxFc(p.t)},${pyFn(p.y)}`)].join("L");

  const recX1 = pxFn(nGood);
  const recW  = pxFn(N_PERIODS) - recX1;

  // ── Output bar ─────────────────────────────────────────────────────────────
  const BAR_H = 130;
  const bh = y => Math.max(4, Math.min(BAR_H, (y - 0.965) / 0.060 * BAR_H));

  const yTicks = [0.972, 0.978, 0.984, 0.990, 0.996, 1.002, 1.008, 1.014, 1.020];

  return (
    <div style={{
      fontFamily:"'Palatino Linotype',Georgia,serif",
      background:C.gb, minHeight:"100vh",
      padding:"16px 16px 24px", color:C.bd,
    }}>

      {/* Title */}
      <div style={{ textAlign:"center", marginBottom:14 }}>
        <div style={{ fontSize:19, fontWeight:"bold", letterSpacing:0.2 }}>
          Endogenous Plucking Through Networks
        </div>
        <div style={{ fontSize:11, color:C.gt, marginTop:3, fontStyle:"italic" }}>
          α accumulates over {nGood} boom periods — denser network, deeper pluck
        </div>
      </div>

      {/* shock hit 控制条 */}
      <div style={{ maxWidth: 600, margin: '0 auto 12px', padding: 4 }}>
        <div style={{ fontSize: 12, color: C.gt, marginBottom: 2 }}>Shock hits at period: <b style={{color:C.or}}>{nGood+1}</b></div>
        <input type="range" min={1} max={N_PERIODS-1} value={nGood} step={1}
          onChange={e => setNGood(Number(e.target.value))}
          style={{ width: '100%', accentColor: C.or }} />
        <div style={{ fontSize: 10, color: C.gt, marginTop: 2 }}>
          (Move to adjust when recession starts)
        </div>
      </div>
      {/* ── Main layout: left col + right col ── */}
      <div style={{ display:"flex", gap:14, justifyContent:"center",
                    alignItems:"stretch", flexWrap:"wrap" }}>

        {/* ── LEFT COLUMN: network diagram stacked on t-Y plot ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:10,
                      flex:"0 0 500px" }}>

          {/* Network diagram */}
          <div style={{ background:C.wh, borderRadius:12,
                        boxShadow:"0 2px 14px rgba(31,56,100,0.09)",
                        padding:"10px 16px 8px" }}>
            <div style={{ display:"flex", justifyContent:"space-between",
                          alignItems:"baseline", marginBottom:4 }}>
              <span style={{ fontSize:10, color:C.gt, letterSpacing:0.7,
                             textTransform:"uppercase" }}>
                Network Sector [0,&thinsp;1]
              </span>
              <span style={{ fontSize:12, fontWeight:"bold",
                             color: pd.isRec ? C.or : C.bm }}>
                {pd.isRec ? "🟠 Recession · A = B" : "🔵 Boom · A = G"}
                &ensp;·&ensp;period {period+1}/{N_PERIODS}
              </span>
            </div>

            <svg width={DW} height={DH} style={{ display:"block", overflow:"visible" }}>
              <defs>
                {[C.bm, C.or].map(col => (
                  <marker key={col} id={`ah${col.replace(/[^a-z0-9]/gi,"")}`}
                    markerWidth="5" markerHeight="4" refX="4.2" refY="2" orient="auto">
                    <polygon points="0 0,5 2,0 4" fill={col} opacity="0.75"/>
                  </marker>
                ))}
              </defs>

              {/* Line */}
              <line x1={LX1} y1={LY} x2={LX2} y2={LY} stroke={C.gl} strokeWidth={2.5}/>
              {[0,.2,.4,.6,.8,1].map(p=>(
                <g key={p}>
                  <line x1={fx(p)} y1={LY-4} x2={fx(p)} y2={LY+4}
                        stroke={C.gl} strokeWidth={1.5}/>
                  <text x={fx(p)} y={LY+14} textAnchor="middle"
                        fontSize={10} fill={C.gt}>{p.toFixed(1)}</text>
                </g>
              ))}

              {/* Supplier dots */}
              {ALL_FIRMS.map((s,i)=>{
                const active = arrowSets.some(({sups})=>sups.includes(s));
                return (
                  <circle key={i} cx={fx(s)} cy={LY}
                    r={active ? 4.5 : 2.5}
                    fill={active ? arrowCol : C.gl}
                    opacity={active ? 0.85 : 0.4}
                    style={{ transition:"r 0.2s,fill 0.2s,opacity 0.2s" }}/>
                );
              })}

              {/* Arrows */}
              {arrowSets.map(({rid,sups})=>
                sups.map((s,si)=>{
                  const dist = Math.abs(s-rid);
                  return (
                    <Arrow key={`${rid}-${si}`}
                      x1={fx(s)} y1={LY-5}
                      x2={fx(rid)+(s<rid?9:-9)} y2={LY-9}
                      color={arrowCol}
                      op={0.20+0.58*(1-dist)}
                      sw={0.85+0.75*(1-dist)}
                      mid={midId}/>
                  );
                })
              )}

              {/* Receiver firms on the line */}
              {RECV_POS.map(rid=>(
                <g key={rid}>
                  <circle cx={fx(rid)} cy={LY} r={12} fill={C.bd}
                    style={{ filter:"drop-shadow(0 1px 4px rgba(31,56,100,0.3))" }}/>
                  <text x={fx(rid)} y={LY+4.5} textAnchor="middle"
                        fontSize={9} fill={C.wh} fontWeight="bold">j</text>
                </g>
              ))}

              {/* α bracket */}
              {(()=>{
                const bx2 = LX1 + alpha * lineLen;
                const by  = LY + 26;
                return (
                  <g>
                    <line x1={LX1+2} y1={by} x2={bx2} y2={by}
                          stroke={C.or} strokeWidth={2.5} strokeLinecap="round"/>
                    {[LX1+2, bx2].map((bx,i)=>
                      <line key={i} x1={bx} y1={by-4} x2={bx} y2={by+4}
                            stroke={C.or} strokeWidth={2}/>
                    )}
                    <text x={(LX1+2+bx2)/2} y={by-6} textAnchor="middle"
                          fontSize={11} fill={C.or} fontWeight="bold">
                      α
                    </text>
                  </g>
                );
              })()}
            </svg>
          </div>

          {/* t-Y plot */}
          <div style={{ background:C.wh, borderRadius:12,
                        boxShadow:"0 2px 14px rgba(31,56,100,0.09)",
                        padding:"10px 16px 8px",
                        flex:"1 1 0",
                        display:"flex", flexDirection:"column" }}>
            <div style={{ display:"flex", justifyContent:"space-between",
                          alignItems:"baseline", marginBottom:4 }}>
              <span style={{ fontSize:10, color:C.gt, letterSpacing:0.7,
                             textTransform:"uppercase" }}>Output over time</span>
              <span style={{ fontSize:10, color:C.gt }}>
                <span style={{ color:C.bl }}>━</span> history&ensp;
                <span style={{ color:C.or, opacity:0.6 }}>▧</span> forecast CI&ensp;
                <span style={{ color:C.or }}>■</span> recession
              </span>
            </div>

            <svg width="100%" viewBox={`0 0 ${PW} ${PH}`}
                 style={{ display:"block", overflow:"hidden", flex:"1 1 0" }}>

              <defs>
                <clipPath id="plotClip">
                  <rect x={PM.l} y={PM.t} width={pw} height={ph}/>
                </clipPath>
              </defs>

              {/* Recession shading */}
              <rect x={recX1} y={PM.t} width={recW} height={ph} fill={C.sh}/>
              <text x={recX1+recW/2} y={PM.t+10} textAnchor="middle"
                    fontSize={9} fill={C.or} opacity={0.7}>recession</text>

              {/* Gridlines */}
              {yTicks.map(yv=>(
                <g key={yv}>
                  <line x1={PM.l} y1={pyFn(yv)} x2={PM.l+pw} y2={pyFn(yv)}
                        stroke={C.gl} strokeWidth={0.7} strokeDasharray="3 3"/>
                  <text x={PM.l-5} y={pyFn(yv)+4} textAnchor="end"
                        fontSize={9} fill={C.gt}>{yv.toFixed(3)}</text>
                </g>
              ))}

              {/* Axes */}
              <line x1={PM.l} y1={PM.t} x2={PM.l} y2={PM.t+ph}
                    stroke={C.gl} strokeWidth={1.2}/>
              <line x1={PM.l} y1={PM.t+ph} x2={PM.l+pw} y2={PM.t+ph}
                    stroke={C.gl} strokeWidth={1.2}/>

              {/* X ticks */}
              {Array.from({length:N_PERIODS},(_,t)=>(
                <g key={t}>
                  <line x1={pxFn(t)} y1={PM.t+ph} x2={pxFn(t)} y2={PM.t+ph+4}
                        stroke={C.gl} strokeWidth={1}/>
                  <text x={pxFn(t)} y={PM.t+ph+14} textAnchor="middle"
                        fontSize={9} fill={t===period ? (pd.isRec?C.or:C.bm) : C.gt}
                        fontWeight={t===period ? "bold" : "normal"}>
                    {t+1}
                  </text>
                </g>
              ))}
              <text x={PM.l+pw/2} y={PH-4} textAnchor="middle"
                    fontSize={9} fill={C.gt}>period</text>

              {/* Forecast CI — clipped to plot area */}
              <g clipPath="url(#plotClip)">
                <path d={fanPath} fill={C.or} opacity={0.10}/>
                <path d={fcGPath} fill="none" stroke={C.bm} strokeWidth={1.2}
                      strokeDasharray="4 3" opacity={0.55}/>
                <path d={fcBPath} fill="none" stroke={C.or} strokeWidth={1.2}
                      strokeDasharray="4 3" opacity={0.55}/>
              </g>

              {/* History path + dots — clipped */}
              <g clipPath="url(#plotClip)">
                {histPath && (
                  <path d={histPath} fill="none" stroke={C.bl} strokeWidth={2.2}/>
                )}
                {histDots.map((d,i)=>(
                  <circle key={i} cx={d.x} cy={d.y} r={3.5}
                    fill={d.rec ? C.or : C.bd} opacity={0.85}/>
                ))}
                <circle cx={curDot.x} cy={curDot.y} r={5.5}
                  fill={pd.isRec ? C.or : C.bd}
                  style={{ filter:"drop-shadow(0 1px 4px rgba(0,0,0,0.25))" }}/>
              </g>

              <text x={PM.l-22} y={pyFn((yMin+yMax)/2)} textAnchor="middle"
                    fontSize={10} fill={C.gt}
                    transform={`rotate(-90,${PM.l-22},${pyFn((yMin+yMax)/2)})`}>
                Y
              </text>
            </svg>
          </div>

          {/* Insight */}
          <div style={{
            background:C.wh, borderRadius:12,
            boxShadow:"0 2px 12px rgba(31,56,100,0.08)",
            padding:"10px 16px",
            borderLeft:`3px solid ${alpha>0.5 ? C.or : C.bm}`,
            transition:"border-color 0.4s",
            fontSize:11, lineHeight:1.75, color:C.bd,
            height:"3.8em", overflow:"hidden",
            display:"flex", alignItems:"center",
          }}>
            <div>
            {!pd.isRec && alpha<0.35 &&
              <><strong>Early boom.</strong> Network sparse — few linkages per firm.<br/>
              TFP multiplier is low. Fragility is limited.</>}
            {!pd.isRec && alpha>=0.35 && alpha<0.6 &&
              <><strong>Boom building.</strong> Linkages deepening across the continuum.<br/>
              Network multiplier rising — fragility accumulates.</>}
            {!pd.isRec && alpha>=0.6 &&
              <><strong style={{color:C.or}}>Mature boom — fragile.</strong> Firms densely connected.<br/>
              A bad shock now amplified far more than at boom start.</>}
            {pd.isRec &&
              <><strong style={{color:C.or}}>Recession.</strong> Inherited α amplifies the contraction.<br/>
              Network linkages slowly unwind during recovery.</>}
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN: output bars + controls ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:10,
                      flex:"0 0 200px", justifyContent:"space-between" }}>

          {/* Output bars */}
          <div style={{ background:C.wh, borderRadius:12,
                        boxShadow:"0 2px 14px rgba(31,56,100,0.09)",
                        padding:"12px 20px",
                        display:"flex", flexDirection:"column",
                        alignItems:"center" }}>
            <div style={{ fontSize:10, color:C.gt, letterSpacing:0.7,
                          textTransform:"uppercase", marginBottom:10 }}>
              Output Y
            </div>

            {/* Realized TFP badge */}
            <div style={{
              width:"100%", marginBottom:10,
              background: pd.isRec ? "#FFF4E8" : "#EEF3FA",
              borderRadius:7, padding:"6px 10px",
              border: `1.5px solid ${pd.isRec ? C.or : C.bm}`,
              display:"flex", alignItems:"center", justifyContent:"space-between",
            }}>
              <span style={{ fontSize:10, color:C.gt }}>Realized TFP</span>
              <span style={{
                fontSize:13, fontWeight:"bold",
                color: pd.isRec ? C.or : C.bm,
                transition:"color 0.3s",
              }}>
                A = {pd.isRec ? "B" : "G"}
              </span>
            </div>

            <div style={{ display:"flex", alignItems:"flex-end", gap:14,
                          height:BAR_H+20, marginBottom:8 }}>
              {[
                {label:"Y(α,G)", val:Yg, c:!pd.isRec ? C.bl : C.bd, lt:C.bl, active: !pd.isRec},
                {label:"Y(α,B)", val:Yb, c:pd.isRec ? C.or : C.ol, lt:C.ol, active:  pd.isRec},
              ].map(({label,val,c,lt,active})=>(
                <div key={label} style={{ display:"flex", flexDirection:"column",
                                          alignItems:"center", gap:4 }}>
                  <div style={{
                    width:46, height:bh(val),
                    background: active ? c : lt,
                    borderRadius:"4px 4px 0 0",
                    opacity: active ? 1 : 0.45,
                  }}/>
                  <div style={{ fontSize:10, color:c,
                                fontWeight: active ? "bold" : "normal" }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ width:"100%", height:1, background:C.gl, marginBottom:8 }}/>
            <div style={{ background:"#FFF4E8", borderRadius:7,
                          padding:"6px 10px", textAlign:"center", width:"100%" }}>
              <div style={{ fontSize:10, color:C.or, marginBottom:1 }}>ΔY(α)</div>
              <div style={{ fontSize:13, fontWeight:"bold", color:C.or }}>
                ↑ grows with α
              </div>
            </div>
          </div>

          {/* Playback */}
          <div style={{ background:C.wh, borderRadius:12,
                        boxShadow:"0 2px 12px rgba(31,56,100,0.08)",
                        padding:"12px 16px",
                        display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>setPlaying(p=>!p)}
                style={btnStyle(playing ? C.or : C.bd, false)}>
                {playing ? "⏸ Pause" : "▶ Play"}
              </button>
              <button onClick={reset} style={btnStyle(C.gt, true)}>↺</button>
            </div>
            <div>
              <div style={{ fontSize:10, color:C.gt, marginBottom:3 }}>
                Speed: {speed}×
              </div>
              <input type="range" min={0.25} max={4} step={0.25} value={speed}
                onChange={e=>setSpeed(parseFloat(e.target.value))}
                style={{ width:"100%", accentColor:C.bm }}/>
            </div>
            <div>
              <div style={{ fontSize:10, color:C.gt, marginBottom:3 }}>
                Period {period+1}&ensp;
                <span style={{ color: pd.isRec ? C.or : C.bm, fontWeight:"bold" }}>
                  ({pd.isRec ? "recession" : "boom"})
                </span>
              </div>
              <input type="range" min={0} max={TOTAL_FRAMES-1} step={1} value={frame}
                onChange={e=>{ setFrame(parseInt(e.target.value)); setPlaying(false); }}
                style={{ width:"100%", accentColor:C.bd }}/>
              <div style={{ display:"flex", justifyContent:"space-between",
                            fontSize:9, marginTop:2 }}>
                {Array.from({length:N_PERIODS},(_,i)=>(
                  <span key={i} style={{
                    color: i===period ? (pd.isRec?C.or:C.bm) : C.gt,
                    fontWeight: i===period ? "bold" : "normal",
                  }}>{i+1}</span>
                ))}
              </div>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
}
